import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { CompactPreview } from './CompactPreview';
import { CompactEdit } from './CompactEdit';
import { useEvaluationStore } from '../../../store/evaluationStore';
import './DifferenceExplanationDialog.css';

interface DifferenceQuestion {
  id: string;
  criterionTitle: string;
  productA: string;
  gradeA: string;
  productB: string;
  gradeB: string;
}

interface DifferenceExplanationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (explanations: { questionId: string; explanation: string }[]) => void;
  questions: DifferenceQuestion[];
}

export const DifferenceExplanationDialog: React.FC<DifferenceExplanationDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
  questions
}) => {
  const { t } = useTranslation();
  const { products } = useEvaluationStore();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [explanations, setExplanations] = useState<{ [key: string]: string }>({});
  const [currentExplanation, setCurrentExplanation] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCurrentQuestionIndex(0);
      setExplanations({});
      setCurrentExplanation('');
    }
  }, [isOpen]);

  // 現在の質問から製品インデックスを取得
  const getProductIndices = (question: DifferenceQuestion) => {
    const parts = question.id.split('-');
    return {
      productAIndex: parseInt(parts[1]),
      productBIndex: parseInt(parts[2])
    };
  };

  if (!isOpen || questions.length === 0) return null;

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  
  // 現在の質問の製品情報を取得
  const { productAIndex, productBIndex } = getProductIndices(currentQuestion);
  const productA = products[productAIndex];
  const productB = products[productBIndex];

  const handleNext = () => {
    // Save current explanation
    if (currentExplanation.trim()) {
      setExplanations(prev => ({
        ...prev,
        [currentQuestion.id]: currentExplanation
      }));
    }

    if (isLastQuestion) {
      // Complete and submit
      const results = Object.entries(explanations).map(([questionId, explanation]) => ({
        questionId,
        explanation
      }));
      
      // Add current explanation if exists
      if (currentExplanation.trim()) {
        results.push({
          questionId: currentQuestion.id,
          explanation: currentExplanation
        });
      }
      
      onComplete(results);
    } else {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
      setCurrentExplanation(explanations[questions[currentQuestionIndex + 1].id] || '');
    }
  };

  const handleSkip = () => {
    if (isLastQuestion) {
      // Complete without current explanation
      const results = Object.entries(explanations).map(([questionId, explanation]) => ({
        questionId,
        explanation
      }));
      onComplete(results);
    } else {
      // Move to next question without saving
      setCurrentQuestionIndex(prev => prev + 1);
      setCurrentExplanation(explanations[questions[currentQuestionIndex + 1].id] || '');
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="difference-dialog-container" onClick={e => e.stopPropagation()}>
        {/* 左側パネル - 高評価製品 */}
        <div className="difference-dialog-panel difference-dialog-panel--left">
          <div className="panel-header">
            <span className="grade-badge grade-badge--high">{currentQuestion.gradeA}</span>
            <span className="product-title">{currentQuestion.productA}</span>
          </div>
          <div className="panel-content">
            {productA?.reviewLink ? (
              <CompactPreview
                url={productA.reviewLink}
                title={productA.reviewTitle}
                comment={productA.reviewComment}
              />
            ) : (
              <CompactEdit productIndex={productAIndex} />
            )}
          </div>
        </div>

        {/* 中央パネル - 差異説明 */}
        <div className="difference-dialog-panel difference-dialog-panel--center">
          <div className="dialog-content difference-explanation-dialog">
            <div className="dialog-header">
              <h2>{t('rubricGeneration.differenceDialog.title')}</h2>
              <button className="dialog-close" onClick={onClose} aria-label={t('common.close')}>
                <X size={24} />
              </button>
            </div>

            <div className="dialog-body">
              <div className="question-progress">
                <span>{t('rubricGeneration.differenceDialog.progress', { 
                  current: currentQuestionIndex + 1, 
                  total: questions.length 
                })}</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              <div className="question-content">
                <h3>{t('rubricGeneration.differenceDialog.question', {
                  criterion: currentQuestion.criterionTitle,
                  productA: currentQuestion.productA,
                  gradeA: currentQuestion.gradeA,
                  productB: currentQuestion.productB,
                  gradeB: currentQuestion.gradeB
                })}</h3>

                <div className="question-hint">
                  {t('rubricGeneration.differenceDialog.hint')}
                </div>

                <textarea
                  value={currentExplanation}
                  onChange={(e) => setCurrentExplanation(e.target.value)}
                  placeholder={t('rubricGeneration.differenceDialog.placeholder')}
                  rows={6}
                  className="explanation-textarea"
                  autoFocus
                />
              </div>
            </div>

            <div className="dialog-footer">
              <Button
                variant="secondary"
                onClick={handleSkip}
              >
                {isLastQuestion 
                  ? t('rubricGeneration.differenceDialog.skipAndGenerate')
                  : t('rubricGeneration.differenceDialog.skip')
                }
              </Button>
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!currentExplanation.trim()}
              >
                {isLastQuestion 
                  ? t('rubricGeneration.differenceDialog.completeAndGenerate')
                  : t('rubricGeneration.differenceDialog.next')
                }
              </Button>
            </div>
          </div>
        </div>

        {/* 右側パネル - 低評価製品 */}
        <div className="difference-dialog-panel difference-dialog-panel--right">
          <div className="panel-header">
            <span className="grade-badge grade-badge--low">{currentQuestion.gradeB}</span>
            <span className="product-title">{currentQuestion.productB}</span>
          </div>
          <div className="panel-content">
            {productB?.reviewLink ? (
              <CompactPreview
                url={productB.reviewLink}
                title={productB.reviewTitle}
                comment={productB.reviewComment}
              />
            ) : (
              <CompactEdit productIndex={productBIndex} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};